import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { ChevronLeft, ChevronRight, Info, Search } from "lucide-react";
import { Input } from "@shared/ui/input";
import { Badge } from "@shared/ui/badge";
import { Plan } from "@features/cx/consumerManagement/types";
import {
  useEditConsumer,
  usePlan,
  usePlanSearch,
  useUpdateConsumer,
} from "@features/cx/consumerManagement/hooks";
import { toast } from "@shared/hooks/use-toast";
import { useParams } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
interface PlanSelectionFormProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  stepHelpers?: {
    getStepData: (stepIndex: number) => any;
    setStepData: (stepIndex: number, data: any) => void;
  };
}

// Custom hook for plan data fetching

export function PlanSelectionForm({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  currentStepIndex = 2,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
  stepHelpers,
}: PlanSelectionFormProps) {
  const [consumerCategory, setConsumerCategory] =
    useState<string>("residential");
  const [selectedPlan, setSelectedPlan] = useState<number>();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const editConsumerMutation = useEditConsumer();
  const { consumerId } = useParams<{ consumerId: string }>();
  const hasLoadedData = useRef(false);
  // Use custom hooks
  // const {
  //   data: planList,
  //   isLoading,
  //   error,
  // } = usePlan({
  //   remoteUtilityId: remoteUtilityId.toString(),
  // });

  // Use plan search hook
  const { data: searchResults, isLoading: isSearching } = usePlanSearch({
    remoteUtilityId: remoteUtilityId.toString(),
    searchField: "plan_name",
    searchData: searchQuery,
  });

  // const plans = planList?.result || [];
  // Determine which data to use based on search state
  const filteredPlans = {
    data: searchResults?.result || [],
    isLoading: isSearching,
  };

  // Load saved plan selection on component mount - use same pattern as other steps
  useEffect(() => {
    // Prevent infinite loops by tracking if we've already loaded data
    if (hasLoadedData.current) {
      return;
    }

    console.log(
      "🎯 PlanSelectionForm: Loading data for step:",
      currentStepIndex
    );
    console.log("🎯 PlanSelectionForm: Storage key:", storageKey);

    const loadData = () => {
      let savedStepData = null;

      // Try StepHelpers first
      if (stepHelpers) {
        savedStepData = stepHelpers.getStepData(currentStepIndex);
        console.log("🎯 StepHelpers data plan:", savedStepData);
      }

      // Fallback to localStorage if StepHelpers is empty
      if (!savedStepData || Object.keys(savedStepData).length === 0) {
        try {
          console.log("🎯 Trying localStorage with key:", storageKey);
          const localStorageData = localStorage.getItem(storageKey);
          console.log("🎯 Raw localStorage data exists:", !!localStorageData);

          if (localStorageData) {
            const parsed = JSON.parse(localStorageData);
            console.log("🎯 Parsed localStorage keys:", Object.keys(parsed));
            console.log(
              "🎯 Looking for step data key:",
              currentStepIndex.toString()
            );
            console.log(
              "🎯 Available stepData keys:",
              Object.keys(parsed.stepData || {})
            );

            savedStepData = parsed.stepData?.[currentStepIndex.toString()];
            console.log("🎯 Found step data:", savedStepData);

            // If still no data, log the full structure for debugging
            if (!savedStepData) {
              console.log("🔍 Full stepData structure:", parsed.stepData);
            }
          }
        } catch (error) {
          console.error("❌ localStorage error:", error);
        }
      }

      // Populate plan selection if we have data
      if (savedStepData) {
        console.log("✅ Populating plan selection with:", savedStepData);

        let planId = null;

        if (savedStepData.selectedPlan) {
          planId = savedStepData.selectedPlan.id || savedStepData.planId;
          setSelectedPlan(planId);
          console.log("🎯 Set selected plan ID:", planId);

          if (savedStepData.selectedPlan.category) {
            setConsumerCategory(savedStepData.selectedPlan.category);
          }
        } else if (savedStepData.planId) {
          // Fallback for older data format
          planId = savedStepData.planId;
          setSelectedPlan(planId);
          console.log("🎯 Set selected plan ID (fallback):", planId);
        }

        // Get complete plan data from plan list and update stepHelpers
        if (planId && stepHelpers && filteredPlans?.data?.length > 0) {
          const completePlanData = filteredPlans?.data?.find((p) => p.id === planId);
          if (completePlanData) {
            const updatedStepData = {
              selectedPlan: completePlanData,
              planId: completePlanData.id,
              planName: completePlanData.planName,
              processingFees: completePlanData.processingFees,
              connectionFee: completePlanData.connectionFee,
              securityDeposit: completePlanData.securityDeposit,
              tax: completePlanData.tax,
              taxType: completePlanData.taxType,
              taxTypeDisplay: completePlanData.taxTypeDisplay,
              validity: completePlanData.validity,
              total: completePlanData.depositAmount,
              description: completePlanData.description,
              billingCycle: completePlanData.billingFrequencyDisplay,
              utilityServices: completePlanData.utilityService,
              category: completePlanData.category,
              timestamp: new Date().toISOString(),
              stepIndex: currentStepIndex,
            };

            stepHelpers.setStepData(currentStepIndex, updatedStepData);
            console.log(
              "🎯 Updated stepHelpers with complete plan data from plan list"
            );
          }
        } else {
          // Fallback: save original data to stepHelpers
          if (stepHelpers) {
            stepHelpers.setStepData(currentStepIndex, savedStepData);
            console.log(
              "🎯 Data loaded from localStorage and saved to stepHelpers"
            );
          }
        }

        console.log("✅ Plan selection populated successfully");
      } else {
        console.log("ℹ️ No saved plan data found for step:", currentStepIndex);
      }

      // Mark as loaded to prevent infinite loops
      hasLoadedData.current = true;
    };

    // Try immediately, but if no data, retry after a short delay
    loadData();

    // Retry mechanism in case of timing issues
    const retryTimer = setTimeout(() => {
      console.log("🔄 Retrying plan data load...");
      loadData();
    }, 200);

    return () => {
      clearTimeout(retryTimer);
      // Reset the loaded flag when component unmounts or step changes
      hasLoadedData.current = false;
    };
  }, [filteredPlans?.data, currentStepIndex, storageKey]); // Removed stepHelpers from dependencies

  const handlePlanSelection = (plan: Plan) => {
    console.log("debug Plan being selected:", plan);
    setSelectedPlan(plan.id);

    // Get complete plan data from plan list
    const completePlanData = filteredPlans?.data.find((p) => p.id === plan.id);
    console.log('debug complete plan data', completePlanData);

    if (completePlanData && stepHelpers) {
      const stepData = {
        selectedPlan: completePlanData,
        planId: completePlanData.id,
        planName: completePlanData.planName,
        processingFees: completePlanData.processingFees,
        connectionFee: completePlanData.connectionFee,
        securityDeposit: completePlanData.securityDeposit,
        tax: completePlanData.tax,
        taxType: completePlanData.taxType,
        taxTypeDisplay: completePlanData.taxTypeDisplay,
        validity: completePlanData.validity,
        total: completePlanData.depositAmount,
        description: completePlanData.description,
        billingCycle: completePlanData.billingFrequencyDisplay,
        utilityServices: completePlanData.utilityService,
        category: completePlanData.category,
        timestamp: new Date().toISOString(),
        stepIndex: currentStepIndex,
      };

      stepHelpers.setStepData(currentStepIndex, stepData);

      // Also save to storage key for backup
      try {
        const existingData = localStorage.getItem(storageKey);
        const allStepData = existingData ? JSON.parse(existingData) : {};
        allStepData[currentStepIndex] = {
          ...allStepData[currentStepIndex],
          ...stepData,
        };
        localStorage.setItem(storageKey, JSON.stringify(allStepData));
        console.log("consumerPlanStepData selection saved to storage:",  JSON.stringify(allStepData));

        
        console.log("Plan selection saved to storage:", stepData);
      } catch (error) {
        console.error("Failed to save plan selection to storage:", error);
      }
    }

    toast({
      title: `Plan "${plan.planName}" selected successfully`,
      variant: "default",
    });
  };

  // Check if a plan is selected
  // Helper function to calculate total fees
  const calculateTotalFees = (plan: Plan) => {
    const connectionFee = plan?.connectionFee || 0;
    const depositAmount = plan?.depositAmount || 0;
    const processingFees = plan?.processingFees || 0;
    const securityDeposit = plan?.securityDeposit || 0;

    return connectionFee + depositAmount + processingFees + securityDeposit;
  };

  const isPlanSelected = (planId: number) => {
    const result = selectedPlan === planId;
    return result;
  };

  const handleSubmit = () => {
    if (!selectedPlan) {
      toast({
        title: "Please select a plan to continue",
        variant: "destructive",
      });
      return;
    }

    const selectedPlanObj = filteredPlans?.data?.find((p) => p.id === selectedPlan);
    if (selectedPlanObj) {
      console.log("Proceeding with plan:", selectedPlanObj);
    }

    editConsumerMutation.mutate({
      type: (currentStepIndex + 1).toString(),
      consumerId: consumerId,
      payload: {
        remotePlanId: selectedPlan,
        completePlanData: selectedPlanObj,
      },
    });
    logTimeUntilStep('Payment Selection')
    // Call onNext if provided and successful
    if (onNext) {
      onNext();
    }
  };

  if (!filteredPlans?.data) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <p className="text-destructive">
            Error loading plans. Please try again.
          </p>
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="mt-4"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      <Accordion type="single" collapsible className="mb-6">
        <AccordionItem
          value="plan-guide"
          className="border border-blue-200 rounded-lg bg-blue-50"
        >
          <AccordionTrigger className="px-4 py-3 hover:bg-blue-100 rounded-t-lg">
            <div className="flex items-center">
              <Info className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
              <span className="font-medium text-blue-800">
                Plan Selection Guide
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <p className="text-blue-700 text-sm">
              Choose the plan that best fits your water usage needs. All plans
              include standard service features, with premium options available
              for additional benefits.
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Selected Plan Display */}
    

      {/* Search bar for plans */}
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search plans..."
          className="pl-9"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* No Results */}
      {!filteredPlans.isLoading && filteredPlans.data.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          {searchQuery
            ? "No plans found matching your search. Try different keywords."
            : "No plans available for the selected category."}
        </div>
      )}

      {/* Plan Cards */}
      {!filteredPlans.isLoading && filteredPlans.data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlans.data.map((plan) => (
            <Card
              key={plan.id}
              className={`relative flex flex-col cursor-pointer transition-all hover:shadow-md ${
                isPlanSelected(plan.id)
                  ? "border-primary bg-primary/5 shadow-md ring-2 ring-primary"
                  : ""
              }`}
              onClick={() => handlePlanSelection(plan)}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-lg">{plan.planName}</CardTitle>
                  <Badge variant="outline" className="text-xs capitalize">
                    {plan.category}
                  </Badge>
                </div>
                <CardDescription className="text-sm">
                  {plan.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 flex-grow">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    ${calculateTotalFees(plan).toFixed(2)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Total Fees
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium mb-2">
                    Utility Services:
                  </h4>

                  <ul className="space-y-1">
                    {(() => {
                      if (!plan.utilityService) {
                        return (
                          <li className="text-sm text-muted-foreground">
                            No services specified
                          </li>
                        );
                      }

                      let services: string[] = [];

                      if (typeof plan.utilityService === "string") {
                        services = plan.utilityService
                          .split(",")
                          .map((s) => s.trim());
                      } else if (Array.isArray(plan.utilityService)) {
                        services = (plan.utilityService as any[]).map((s) =>
                          String(s).trim()
                        );
                      } else {
                        services = [String(plan.utilityService)];
                      }

                      if (services.length === 0) {
                        return (
                          <li className="text-sm text-muted-foreground">
                            No services specified
                          </li>
                        );
                      }

                      return (
                        <>
                          <li className="text-xs text-muted-foreground flex justify-between font-medium px-1">
                            <span>Service Name</span>
                            <span>Rate Name</span>
                          </li>
                          {services.map((service: string, idx: number) => {
                            const serviceRate =
                              plan.utilityServiceData?.rate?.[service]?.rate
                                ?.rateName;
                            return (
                              <li
                                key={idx}
                                className="text-sm flex justify-between px-1"
                              >
                                <span>{service}</span>
                                <span className="text-muted-foreground">
                                  {serviceRate || "-"}
                                </span>
                              </li>
                            );
                          })}
                        </>
                      );
                    })()}
                  </ul>
                </div>

                {/* {plan.eligibility && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Eligibility:</h4>
                    <ul className="space-y-2">
                      {plan.eligibility.map((item, index) => (
                        <li key={`eligibility-${index}`} className="flex items-start text-amber-600">
                          <Info className="h-4 w-4 mr-2 mt-1 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div> */}
                {/* )} */}
              </CardContent>

              <CardFooter className="pt-4">
                <Button
                  variant={isPlanSelected(plan.id) ? "default" : "outline"}
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlanSelection(plan);
                  }}
                >
                  {isPlanSelected(plan.id) ? "Selected" : "Select Plan"}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
        {selectedPlan && selectedPlan !== 0 && (
        <div className="p-3 bg-primary/5 border border-primary/20 rounded-md">
          <p className="text-sm font-medium text-primary">
            Selected: {filteredPlans?.data?.find((p) => p.id === selectedPlan)?.planName}
          </p>
          <p className="text-xs text-muted-foreground">
            Total Fees: $
            {calculateTotalFees(
              filteredPlans?.data?.find((p) => p.id === selectedPlan) || ({} as Plan)
            ).toFixed(2)}
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <Button
          onClick={onPrevious}
          variant="outline"
          className="flex items-center space-x-2"
          disabled={isValidating}
        >
          <ChevronLeft size={16} />
          <span>Previous</span>
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={isValidating || !selectedPlan || filteredPlans.isLoading}
          className="flex items-center space-x-2"
        >
          <span>Save and Continue</span>
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
}